/**
 * OTP — Coleções em destaque (deck).
 *
 * Scoped to <otp-collection-deck>. Touches nothing else on the page and pulls
 * in no library. Moving the deck only rewrites two custom properties per card
 * (--d and --a); the stylesheet does the rest on the compositor.
 */

class OtpCollectionDeck extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = 'true';

    this.stage = this.querySelector('[data-stage]');
    this.cards = Array.from(this.querySelectorAll('[data-card]'));
    this.dots = Array.from(this.querySelectorAll('[data-dot]'));
    if (!this.stage || this.cards.length === 0) return;

    this.count = this.cards.length;
    this.active = 0;
    this.dragged = false;

    this.bindPointer();
    this.bindClicks();
    this.bindArrows();
    this.bindKeys();
    this.bindEditor();
    this.render();
  }

  /* signed distance around the ring, so the deck wraps both ways */
  distance(index) {
    let d = index - this.active;
    const half = this.count / 2;
    if (d > half) d -= this.count;
    if (d < -half) d += this.count;
    return d;
  }

  render() {
    /* how many neighbours stay on screen on each side */
    const depth = this.count > 2 ? 2 : 1;

    this.cards.forEach((card, index) => {
      const d = this.distance(index);
      const a = Math.abs(d);
      const shown = a <= depth;

      card.style.setProperty('--d', String(d));
      card.style.setProperty('--a', String(Math.min(a, depth + 1)));
      card.style.zIndex = String(100 - a);
      card.classList.toggle('is-active', d === 0);
      card.classList.toggle('is-out', !shown);
      card.setAttribute('aria-hidden', shown ? 'false' : 'true');

      /* only the centre card is a tab stop; the arrows move the rest into place */
      const link = card.querySelector('a');
      if (link) link.tabIndex = d === 0 ? 0 : -1;
    });

    this.dots.forEach((dot, index) => {
      const on = index === this.active;
      dot.classList.toggle('is-on', on);
      dot.setAttribute('aria-current', on ? 'true' : 'false');
    });
  }

  setActive(index) {
    this.active = ((index % this.count) + this.count) % this.count;
    this.render();
  }

  move(direction) {
    this.setActive(this.active + direction);
  }

  /* ---------- swipe / drag ---------- */
  bindPointer() {
    let startX = null;
    let startY = null;
    let horizontal = false;

    this.stage.addEventListener(
      'pointerdown',
      (event) => {
        startX = event.clientX;
        startY = event.clientY;
        horizontal = false;
        this.dragged = false;
      },
      { passive: true }
    );

    this.stage.addEventListener(
      'pointermove',
      (event) => {
        if (startX === null) return;
        const dx = event.clientX - startX;
        const dy = event.clientY - startY;
        /* touch-action: pan-y already lets the page scroll vertically */
        if (!horizontal && Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy)) horizontal = true;
      },
      { passive: true }
    );

    const end = (event) => {
      if (startX === null) return;
      const dx = event.clientX - startX;
      if (horizontal && Math.abs(dx) > 40) {
        this.move(dx < 0 ? 1 : -1);
        this.dragged = true;
      }
      startX = null;
      startY = null;
      horizontal = false;
    };

    this.stage.addEventListener('pointerup', end, { passive: true });
    this.stage.addEventListener('pointercancel', () => {
      startX = null;
      horizontal = false;
    }, { passive: true });
  }

  /* ---------- clicks ---------- */
  bindClicks() {
    /* capture, so a side card is brought forward instead of following its link,
       and so the click that ends a swipe never navigates */
    this.stage.addEventListener(
      'click',
      (event) => {
        if (this.dragged) {
          event.preventDefault();
          event.stopPropagation();
          this.dragged = false;
          return;
        }
        const card = event.target.closest('[data-card]');
        if (!card) return;
        const index = this.cards.indexOf(card);
        if (index < 0 || index === this.active) return;
        event.preventDefault();
        event.stopPropagation();
        this.setActive(index);
      },
      true
    );

    /* tabbing into a card that is off to the side brings it forward */
    this.stage.addEventListener('focusin', (event) => {
      const card = event.target.closest('[data-card]');
      const index = card ? this.cards.indexOf(card) : -1;
      if (index >= 0 && index !== this.active) this.setActive(index);
    });

    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.setActive(index));
    });
  }

  bindArrows() {
    const prev = this.querySelector('[data-prev]');
    const next = this.querySelector('[data-next]');
    if (prev) prev.addEventListener('click', () => this.move(-1));
    if (next) next.addEventListener('click', () => this.move(1));
  }

  bindKeys() {
    this.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.move(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.move(1);
      }
    });
  }

  /* selecting a block in the theme editor brings that card to the centre */
  bindEditor() {
    document.addEventListener('shopify:block:select', (event) => {
      const index = this.cards.indexOf(event.target);
      if (index >= 0) this.setActive(index);
    });
  }
}

if (!customElements.get('otp-collection-deck')) {
  customElements.define('otp-collection-deck', OtpCollectionDeck);
}
